import React, { useState } from 'react';
import { makeStyles, shorthands, tokens, TabList, Tab, Button, Input, Label, ToggleButton, SearchBox, Textarea } from '@fluentui/react-components';
import { Code20Regular, TextBulletListSquare20Regular, DocumentText20Regular } from '@fluentui/react-icons';
import { booqableEndpoints } from '../config/booqable-endpoints';
import { linxupEndpoints } from '../config/linxup-endpoints';
import { coreEndpoints } from '../config/core-endpoints';
import { apiFetch } from '../lib/apiFetch';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    ...shorthands.padding('0', '24px'),
  },
  section: {
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  tabList: {
    marginBottom: '24px',
  },
  tabContent: {
    ...shorthands.padding('16px', '0'),
    display: 'flex',
    gap: '16px',
    height: 'calc(100vh - 400px)',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  endpointList: {
    display: 'flex',
    flexDirection: 'column',
    width: '25%',
    minWidth: '0',
    flexShrink: 0,
    ...shorthands.padding('0', '16px', '0', '0'),
    transition: 'width 0.3s ease, margin-left 0.3s ease',
  },
  methodFilterContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    position: 'sticky',
    top: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 10,
    ...shorthands.padding('8px', '0'),
  },
  endpointScrollContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    overflowY: 'auto',
    flex: 1,
  },
  methodFilterButton: {
    minWidth: '60px',
  },
  requestResponseContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '75%',
    gap: '16px',
    minWidth: '0',
    overflow: 'hidden',
  },
  requestColumn: {
    width: '100%',
    minWidth: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
  },
  resultsColumn: {
    width: '100%',
    minWidth: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: 1,
    ...shorthands.padding('24px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    overflow: 'hidden',
  },
  columnTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
  },
  resultsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  resultsTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusPill: {
    ...shorthands.padding('4px', '12px'),
    ...shorthands.borderRadius('12px'),
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
  },
  statusPillSuccess: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  statusPillError: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  timePill: {
    ...shorthands.padding('4px', '12px'),
    ...shorthands.borderRadius('12px'),
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: tokens.colorNeutralBackground5,
    color: tokens.colorNeutralForeground2,
  },
  resultsControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  searchBox: {
    width: '200px',
  },
  viewToggle: {
    display: 'flex',
    gap: '4px',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  formFieldRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-end',
  },
  formFieldInput: {
    flex: 1,
  },
  jsonInput: {
    fontFamily: 'monospace',
    fontSize: '12px',
    minHeight: '120px',
    resize: 'vertical',
  },
  jsonResults: {
    flex: 1,
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    fontFamily: 'monospace',
    fontSize: '12px',
    overflowY: 'auto',
    whiteSpace: 'pre-wrap',
    color: tokens.colorNeutralForeground1,
  },
  formattedResults: {
    flex: 1,
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    fontSize: '13px',
    overflowY: 'auto',
    color: tokens.colorNeutralForeground1,
  },
  dataCard: {
    ...shorthands.padding('12px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    marginBottom: '12px',
  },
  dataCardHeader: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorBrandForeground1,
    ...shorthands.padding('8px', '0'),
    marginBottom: '8px',
    ...shorthands.borderBottom('2px', 'solid', tokens.colorBrandStroke1),
  },
  dataRow: {
    display: 'flex',
    ...shorthands.padding('6px', '0'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    '&:last-child': {
      ...shorthands.borderBottom('0'),
    },
  },
  dataLabel: {
    fontWeight: '600',
    minWidth: '150px',
    color: tokens.colorNeutralForeground2,
  },
  dataValue: {
    flex: 1,
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
  },
  dataLink: {
    color: tokens.colorBrandForeground1,
    textDecoration: 'underline',
    cursor: 'pointer',
    position: 'relative',
    '&:hover': {
      color: tokens.colorBrandForeground2,
    },
  },
  imageLinkWrapper: {
    position: 'relative',
    display: 'inline-block',
  },
  imageTooltip: {
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%) translateY(-8px)',
    ...shorthands.padding('8px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    zIndex: 1000,
    pointerEvents: 'none',
    maxWidth: '300px',
    animationDuration: '0.2s',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'forwards',
  },
  imageTooltipEnter: {
    animationName: {
      '0%': {
        opacity: 0,
        transform: 'translateX(-50%) translateY(-4px)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateX(-50%) translateY(-8px)',
      },
    },
  },
  imageTooltipExit: {
    animationName: {
      '0%': {
        opacity: 1,
        transform: 'translateX(-50%) translateY(-8px)',
      },
      '100%': {
        opacity: 0,
        transform: 'translateX(-50%) translateY(-4px)',
      },
    },
  },
  thumbnailImage: {
    maxWidth: '100%',
    maxHeight: '200px',
    display: 'block',
    ...shorthands.borderRadius('4px'),
  },
  categoryTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginTop: '16px',
    marginBottom: '8px',
  },
  endpointItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground3,
      ...shorthands.border('1px', 'solid', tokens.colorBrandStroke1),
    },
  },
  endpointItemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorBrandStroke1),
  },
  endpointPath: {
    fontSize: '14px',
    fontWeight: '600',
    color: tokens.colorBrandForeground1,
    fontFamily: 'monospace',
  },
  endpointDescription: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
});

interface EndpointData {
  path: string;
  description: string;
}

const ApiTools: React.FC = () => {
  const styles = useStyles();
  const [selectedTab, setSelectedTab] = useState<string>('core');
  const [selectedEndpoint, setSelectedEndpoint] = useState<EndpointData | null>(null);
  const [requestParams, setRequestParams] = useState<Record<string, string>>({});
  const [requestBody, setRequestBody] = useState<string>('');
  const [results, setResults] = useState<string>('');
  const [resultCount, setResultCount] = useState<number>(0);
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const [responseTime, setResponseTime] = useState<number | null>(null);
  const [responseHeaders, setResponseHeaders] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'formatted' | 'json' | 'raw'>('formatted');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [methodFilter, setMethodFilter] = useState<string>('ALL');

  const extractPathParams = (path: string): string[] => {
    const matches = path.match(/\{([^}]+)\}/g);
    return matches ? matches.map(m => m.replace(/[{}]/g, '')) : [];
  };

  const handleEndpointSelect = (endpoint: EndpointData) => {
    setSelectedEndpoint(endpoint);
    setRequestParams({});
    setRequestBody('');
    setResults('');
    setResultCount(0);
    setStatusCode(null);
    setResponseTime(null);
    setResponseHeaders({});
  };

  const handleEndpointChange = (value: string) => {
    if (selectedEndpoint) {
      setSelectedEndpoint({ ...selectedEndpoint, path: value });
    }
  };

  const handleSendRequest = async () => {
    if (!selectedEndpoint) return;

    setIsLoading(true);
    const startTime = performance.now();
    try {
      // Extract HTTP method and path
      const pathParts = selectedEndpoint.path.split(' ');
      const method = pathParts[0];
      let finalPath = pathParts.slice(1).join(' ');

      // Replace path parameters
      Object.keys(requestParams).forEach(param => {
        finalPath = finalPath.replace(`{${param}}`, requestParams[param]);
      });

      // Core hits the TWM Flask backend directly via apiFetch; everything else
      // funnels through /booqable-proxy (which forwards to Booqable / Linxup).
      const isCore = selectedTab === 'core';
      const hasBody = method === 'POST' || method === 'PUT' || method === 'PATCH';

      let response: Response;
      if (isCore) {
        const directOptions: RequestInit = {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: hasBody && requestBody ? requestBody : undefined,
        };
        response = await apiFetch(finalPath, directOptions);
      } else {
        const proxyOptions: RequestInit = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            method: method,
            path: finalPath,
            body: requestBody ? requestBody : undefined,
          }),
        };
        response = await apiFetch(`/booqable-proxy`, proxyOptions);
      }

      const endTime = performance.now();
      const data = await response.json();

      setStatusCode(response.status);
      setResponseTime(Math.round(endTime - startTime));

      if (isCore) {
        const directHeaders: Record<string, string> = {};
        response.headers.forEach((value, key) => {
          directHeaders[key] = value;
        });
        setResponseHeaders(directHeaders);
      } else {
        const headersJson = response.headers.get('X-Response-Headers');
        if (headersJson) {
          try {
            setResponseHeaders(JSON.parse(headersJson));
          } catch (e) {
            setResponseHeaders({});
          }
        }
      }

      let count = 0;
      if (data && typeof data === 'object') {
        if (Array.isArray(data.data)) {
          count = data.data.length;
        } else if (data.data && typeof data.data === 'object') {
          count = 1;
        }
      }

      setResultCount(count);
      setResults(JSON.stringify(data, null, 2));
    } catch (error) {
      setResultCount(0);
      setStatusCode(null);
      setResponseTime(null);
      setResponseHeaders({});
      setResults(JSON.stringify({ error: String(error) }, null, 2));
    } finally {
      setIsLoading(false);
    }
  };

  const filterData = (data: any): boolean => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const searchableText = JSON.stringify(data).toLowerCase();
    return searchableText.includes(query);
  };

  const getObjectLabel = (item: any, index: number): string => {
    const type = item.type || 'object';
    const attrs = item.attributes || item;

    const name = attrs.name || attrs.title || attrs.number || attrs.email || attrs.label;

    if (name) {
      return `${type.charAt(0).toUpperCase() + type.slice(1)}: ${name}`;
    }

    return `${type.charAt(0).toUpperCase() + type.slice(1)} #${index + 1}`;
  };

  const isUrl = (str: string): boolean => {
    if (typeof str !== 'string') return false;
    try {
      const url = new URL(str);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const isImageUrl = (url: string): boolean => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
    const lowerUrl = url.toLowerCase();
    return imageExtensions.some(ext => lowerUrl.includes(ext));
  };

  const ImageLinkWithTooltip: React.FC<{ url: string }> = ({ url }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const exitTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = () => {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
        exitTimeoutRef.current = null;
      }
      setIsExiting(false);

      timeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, 500);
    };

    const handleMouseLeave = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (showTooltip) {
        setIsExiting(true);
        exitTimeoutRef.current = setTimeout(() => {
          setShowTooltip(false);
          setIsExiting(false);
        }, 200);
      }
    };

    React.useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (exitTimeoutRef.current) clearTimeout(exitTimeoutRef.current);
      };
    }, []);

    return (
      <span className={styles.imageLinkWrapper}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.dataLink}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {url}
        </a>
        {(showTooltip || isExiting) && (
          <div
            className={`${styles.imageTooltip} ${isExiting ? styles.imageTooltipExit : styles.imageTooltipEnter}`}
          >
            <img
              src={url}
              alt="Preview"
              className={styles.thumbnailImage}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}
      </span>
    );
  };

  const renderDataValue = (value: any): React.ReactNode => {
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    const strValue = String(value);
    if (isUrl(strValue)) {
      if (isImageUrl(strValue)) {
        return <ImageLinkWithTooltip url={strValue} />;
      }

      return (
        <a
          href={strValue}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.dataLink}
          onClick={(e) => e.stopPropagation()}
        >
          {strValue}
        </a>
      );
    }

    return strValue;
  };

  const renderFormattedView = () => {
    if (!results) return 'Response will appear here after sending a request';

    try {
      const data = JSON.parse(results);

      if (data.error) {
        return <div style={{ color: tokens.colorPaletteRedForeground1 }}>Error: {data.error}</div>;
      }

      if (data.data && Array.isArray(data.data)) {
        const filteredData = data.data.filter(filterData);

        if (filteredData.length === 0 && searchQuery) {
          return <div style={{ color: tokens.colorNeutralForeground2 }}>No results match your search</div>;
        }

        return (
          <div>
            {filteredData.map((item: any, idx: number) => (
              <div key={idx} className={styles.dataCard}>
                <div className={styles.dataCardHeader}>
                  {getObjectLabel(item, idx)}
                </div>
                {Object.entries(item.attributes || item).map(([key, value]) => (
                  <div key={key} className={styles.dataRow}>
                    <div className={styles.dataLabel}>{key}:</div>
                    <div className={styles.dataValue}>
                      {renderDataValue(value)}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      }

      if (data.data && typeof data.data === 'object') {
        const item = data.data;

        if (!filterData(item) && searchQuery) {
          return <div style={{ color: tokens.colorNeutralForeground2 }}>No results match your search</div>;
        }

        return (
          <div className={styles.dataCard}>
            <div className={styles.dataCardHeader}>
              {getObjectLabel(item, 0)}
            </div>
            {Object.entries(item.attributes || item).map(([key, value]) => (
              <div key={key} className={styles.dataRow}>
                <div className={styles.dataLabel}>{key}:</div>
                <div className={styles.dataValue}>
                  {renderDataValue(value)}
                </div>
              </div>
            ))}
          </div>
        );
      }

      return results;
    } catch (e) {
      return results;
    }
  };

  const renderJsonView = () => {
    if (!results) return 'Response will appear here after sending a request';

    if (!searchQuery) return results;

    try {
      const data = JSON.parse(results);

      if (data.data && Array.isArray(data.data)) {
        const filteredData = data.data.filter(filterData);
        const filteredResult = { ...data, data: filteredData };
        return JSON.stringify(filteredResult, null, 2);
      } else if (data.data && typeof data.data === 'object') {
        if (!filterData(data.data)) {
          return JSON.stringify({ ...data, data: [] }, null, 2);
        }
      }

      return results;
    } catch (e) {
      return results;
    }
  };

  const filterEndpointsByMethod = (endpoints: any[]) => {
    if (methodFilter === 'ALL') {
      return endpoints;
    }
    return endpoints.filter(endpoint => {
      const method = endpoint.path.split(' ')[0];
      return method === methodFilter;
    });
  };

  const renderApiContent = (endpointsConfig: any[]) => (
    <>
      <div className={styles.endpointList}>
        <div className={styles.methodFilterContainer}>
          <Button size="small" appearance={methodFilter === 'ALL' ? 'primary' : 'secondary'} onClick={() => setMethodFilter('ALL')} className={styles.methodFilterButton}>ALL</Button>
          <Button size="small" appearance={methodFilter === 'GET' ? 'primary' : 'secondary'} onClick={() => setMethodFilter('GET')} className={styles.methodFilterButton}>GET</Button>
          <Button size="small" appearance={methodFilter === 'POST' ? 'primary' : 'secondary'} onClick={() => setMethodFilter('POST')} className={styles.methodFilterButton}>POST</Button>
          <Button size="small" appearance={methodFilter === 'PUT' ? 'primary' : 'secondary'} onClick={() => setMethodFilter('PUT')} className={styles.methodFilterButton}>PUT</Button>
          <Button size="small" appearance={methodFilter === 'DELETE' ? 'primary' : 'secondary'} onClick={() => setMethodFilter('DELETE')} className={styles.methodFilterButton}>DELETE</Button>
        </div>
        <div className={styles.endpointScrollContainer}>
          {endpointsConfig.map((category, idx) => {
            const filteredEndpoints = filterEndpointsByMethod(category.endpoints);
            if (filteredEndpoints.length === 0) return null;

            return (
              <div key={idx}>
                <div className={styles.categoryTitle}>{category.category}</div>
                {filteredEndpoints.map((endpoint, endpointIdx) => (
                  <div
                    key={endpointIdx}
                    className={`${styles.endpointItem} ${selectedEndpoint?.path === endpoint.path ? styles.endpointItemSelected : ''}`}
                    onClick={() => handleEndpointSelect(endpoint)}
                  >
                    <div className={styles.endpointPath}>{endpoint.path}</div>
                    <div className={styles.endpointDescription}>{endpoint.description}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
      <div className={styles.requestResponseContainer}>
        <div className={styles.requestColumn}>
          <div className={styles.columnTitle}>Request</div>
          {selectedEndpoint ? (
            <>
              {(() => {
                const params = extractPathParams(selectedEndpoint.path);
                const method = selectedEndpoint.path.split(' ')[0];
                const isPostRequest = method === 'POST' || method === 'PUT' || method === 'PATCH';

                if (params.length === 0) {
                  return (
                    <>
                      <div className={styles.formFieldRow}>
                        <div className={`${styles.formField} ${styles.formFieldInput}`}>
                          <Label weight="semibold">Endpoint</Label>
                          <Input
                            value={selectedEndpoint.path}
                            onChange={(e) => handleEndpointChange(e.target.value)}
                          />
                        </div>
                        <Button
                          appearance="primary"
                          onClick={handleSendRequest}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Sending...' : 'SEND'}
                        </Button>
                      </div>
                      {isPostRequest && (
                        <div className={styles.formField}>
                          <Label weight="semibold">Request Body (JSON)</Label>
                          <Textarea
                            className={styles.jsonInput}
                            value={requestBody}
                            onChange={(e) => setRequestBody(e.target.value)}
                            placeholder='{"key": "value"}'
                          />
                        </div>
                      )}
                    </>
                  );
                }

                return (
                  <>
                    <div className={styles.formField}>
                      <Label weight="semibold">Endpoint</Label>
                      <Input
                        value={selectedEndpoint.path}
                        onChange={(e) => handleEndpointChange(e.target.value)}
                      />
                    </div>
                    {params.map((param, index) => {
                      const isLastParam = index === params.length - 1;

                      if (isLastParam && !isPostRequest) {
                        return (
                          <div key={param} className={styles.formFieldRow}>
                            <div className={`${styles.formField} ${styles.formFieldInput}`}>
                              <Label weight="semibold">{param}</Label>
                              <Input
                                value={requestParams[param] || ''}
                                onChange={(e) => setRequestParams({ ...requestParams, [param]: e.target.value })}
                                placeholder={`Enter ${param}`}
                              />
                            </div>
                            <Button
                              appearance="primary"
                              onClick={handleSendRequest}
                              disabled={isLoading}
                            >
                              {isLoading ? 'Sending...' : 'SEND'}
                            </Button>
                          </div>
                        );
                      }

                      return (
                        <div key={param} className={styles.formField}>
                          <Label weight="semibold">{param}</Label>
                          <Input
                            value={requestParams[param] || ''}
                            onChange={(e) => setRequestParams({ ...requestParams, [param]: e.target.value })}
                            placeholder={`Enter ${param}`}
                          />
                        </div>
                      );
                    })}
                    {isPostRequest && (
                      <>
                        <div className={styles.formField}>
                          <Label weight="semibold">Request Body (JSON)</Label>
                          <Textarea
                            className={styles.jsonInput}
                            value={requestBody}
                            onChange={(e) => setRequestBody(e.target.value)}
                            placeholder='{"key": "value"}'
                          />
                        </div>
                        <Button
                          appearance="primary"
                          onClick={handleSendRequest}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Sending...' : 'SEND'}
                        </Button>
                      </>
                    )}
                  </>
                );
              })()}
            </>
          ) : (
            <div style={{ color: tokens.colorNeutralForeground2 }}>
              Select an endpoint from the list to configure the request
            </div>
          )}
        </div>
        <div className={styles.resultsColumn}>
          <div className={styles.resultsHeader}>
            <div className={styles.resultsTitleRow}>
              <div className={styles.columnTitle}>
                Results {results && resultCount > 0 && `(${resultCount})`}
              </div>
              {statusCode && (
                <div className={`${styles.statusPill} ${statusCode >= 200 && statusCode < 300 ? styles.statusPillSuccess : styles.statusPillError}`}>
                  {statusCode}
                </div>
              )}
              {responseTime !== null && (
                <div className={styles.timePill}>
                  {responseTime}ms
                </div>
              )}
            </div>
            <div className={styles.resultsControls}>
              {viewMode !== 'raw' && (
                <SearchBox
                  className={styles.searchBox}
                  placeholder="Search results..."
                  value={searchQuery}
                  onChange={(e, data) => setSearchQuery(data?.value || '')}
                  size="small"
                />
              )}
              <div className={styles.viewToggle}>
                <ToggleButton
                  icon={<TextBulletListSquare20Regular />}
                  checked={viewMode === 'formatted'}
                  onClick={() => setViewMode('formatted')}
                  appearance="subtle"
                  size="small"
                />
                <ToggleButton
                  icon={<Code20Regular />}
                  checked={viewMode === 'json'}
                  onClick={() => setViewMode('json')}
                  appearance="subtle"
                  size="small"
                />
                <ToggleButton
                  icon={<DocumentText20Regular />}
                  checked={viewMode === 'raw'}
                  onClick={() => setViewMode('raw')}
                  appearance="subtle"
                  size="small"
                />
              </div>
            </div>
          </div>
          {viewMode === 'formatted' ? (
            <div className={styles.formattedResults}>
              {renderFormattedView()}
            </div>
          ) : viewMode === 'json' ? (
            <div className={styles.jsonResults}>
              {renderJsonView()}
            </div>
          ) : (
            <div className={styles.jsonResults}>
              {results ? (
                <>
                  {Object.keys(responseHeaders).length > 0 && (
                    <>
                      {'===== RESPONSE HEADERS =====\n\n'}
                      {Object.entries(responseHeaders).map(([key, value]) => `${key}: ${value}`).join('\n')}
                      {'\n\n===== RESPONSE BODY =====\n\n'}
                    </>
                  )}
                  {results}
                </>
              ) : (
                'Response will appear here after sending a request'
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className={styles.content}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>API Testing & Monitoring</div>
        <TabList
          className={styles.tabList}
          selectedValue={selectedTab}
          onTabSelect={(_, data) => setSelectedTab(data.value as string)}
        >
          <Tab value="core">Core</Tab>
          <Tab value="booqable">Booqable</Tab>
          <Tab value="linxup">Linxup</Tab>
          <Tab value="authorize-net">authorize.net</Tab>
          <Tab value="quickbooks">QuickBooks</Tab>
          <Tab value="intercom">Intercom</Tab>
        </TabList>
        <div className={styles.tabContent}>
          {selectedTab === 'core' && renderApiContent(coreEndpoints)}
          {selectedTab === 'booqable' && renderApiContent(booqableEndpoints)}
          {selectedTab === 'linxup' && renderApiContent(linxupEndpoints)}
        </div>
      </div>
    </div>
  );
};

export default ApiTools;
